/** Sixteen deterministic pigments extracted locally; alpha composites onto white.
 * Input is bounded RGBA pixels, never an image URL or uploaded file. */
export function quantizeSand(rgba, width, height) {
  if (
    !Number.isInteger(width) ||
    !Number.isInteger(height) ||
    width < 1 ||
    height < 1 ||
    width > 96 ||
    height > 128 ||
    rgba.length !== width * height * 4
  )
    throw new RangeError('Choose a bounded sand image.');
  const samples = Array.from({ length: width * height }, (_, i) =>
    [0, 1, 2].map((c) =>
      Math.round((rgba[i * 4 + c] * rgba[i * 4 + 3]) / 255 + 255 - rgba[i * 4 + 3]),
    ),
  );
  const distance = (a, b) => a.reduce((sum, v, c) => sum + (v - b[c]) ** 2, 0);
  const centres = [samples[0].slice()];
  for (let k = 1; k < 16; k++) {
    let best = samples[0],
      maximum = -1;
    for (const rgb of samples) {
      const d = Math.min(...centres.map((c) => distance(rgb, c)));
      if (d > maximum) {
        maximum = d;
        best = rgb;
      }
    }
    centres.push(best.slice());
  }
  let board = [];
  for (let step = 0; step < 5; step++) {
    const sums = centres.map(() => [0, 0, 0, 0]);
    board = samples.map((rgb) => {
      let best = 0;
      for (let i = 1; i < 16; i++)
        if (distance(rgb, centres[i]) < distance(rgb, centres[best])) best = i;
      rgb.forEach((v, c) => (sums[best][c] += v));
      sums[best][3]++;
      return best;
    });
    sums.forEach((s, i) => {
      if (s[3]) centres[i] = s.slice(0, 3).map((v) => Math.round(v / s[3]));
    });
  }
  board = coherentPigments(board, width, height);
  return { width, height, pixels: board, palette: centres.map((rgb) => `rgb(${rgb.join(',')})`) };
}

/** One local majority pass removes isolated photographic speckles before play.
 * Ties retain the source pigment; no new colour or runtime rearrangement occurs. */
function coherentPigments(pixels, width, height) {
  return pixels.map((colour, index) => {
    const x = index % width,
      y = Math.floor(index / width),
      counts = Array(16).fill(0);
    for (let dy = -1; dy <= 1; dy++)
      for (let dx = -1; dx <= 1; dx++) {
        if (x + dx >= 0 && x + dx < width && y + dy >= 0 && y + dy < height)
          counts[pixels[(y + dy) * width + x + dx]]++;
      }
    return counts.reduce((best, count, pigment) => (count > counts[best] ? pigment : best), colour);
  });
}

/** Create a finite column-gravity puzzle. Every grain has one permanent ID;
 * all colours remain selectable, so an exposed bottom grain always permits progress. */
export function createSandConveyor(image, level = 1) {
  const { width, height, pixels, palette } = image;
  if (
    !Number.isInteger(width) ||
    !Number.isInteger(height) ||
    width < 1 ||
    height < 1 ||
    width > 96 ||
    height > 128 ||
    pixels.length !== width * height ||
    palette.length !== 16 ||
    pixels.some((v) => !Number.isInteger(v) || v < 0 || v > 15)
  )
    throw new RangeError('Invalid sand image.');
  return {
    width,
    height,
    palette: [...palette],
    columns: Array.from({ length: width }, (_, x) =>
      Array.from({ length: height }, (_, y) => ({
        id: y * width + x,
        colour: pixels[y * width + x],
      })),
    ),
    total: pixels.length,
    collected: 0,
    pots: 0,
    collectors: Array(6).fill(null),
    filled: 0,
    score: 0,
    level: Math.max(1, Math.min(100, Math.floor(level))),
    complete: false,
    events: [],
  };
}

/** Choose an exposed pigment for the next empty/oldest collector station.
 * Six persistent pigments drain vertical runs together. Full pots depart and
 * refill automatically; exhausted pigments free their station. Every accepted
 * choice removes at least one unique grain, guaranteeing a finite clear. */
export function collectSand(state, colour) {
  if (!Number.isInteger(colour) || colour < 0 || colour > 15)
    throw new RangeError('Choose a pigment.');
  if (state.complete || !state.columns.some((column) => column.at(-1)?.colour === colour))
    return state;
  const next = {
    ...state,
    columns: state.columns.map((column) => [...column]),
    collectors: state.collectors.map((pot) => pot && { ...pot }),
    events: [],
  };
  const slot = nextSandSlot(state);
  const capacity = sandPotCapacity(state);
  next.collectors[slot] = { colour, amount: 0, since: state.pots + 1 };
  const filledSlots = Array(6).fill(0);
  let removed = 0,
    filled = 0;
  for (let x = 0; x < state.width; x++) {
    const column = next.columns[x];
    while (column.length) {
      const station = next.collectors.findIndex((pot) => pot?.colour === column.at(-1).colour);
      if (station < 0) break;
      const grain = column.pop(),
        pot = next.collectors[station];
      next.events.push({
        ...grain,
        x,
        y: state.height - state.columns[x].length + column.length,
        slot: station,
      });
      removed++;
      pot.amount++;
      if (pot.amount === capacity) {
        filled++;
        filledSlots[station]++;
        pot.amount = 0;
      }
    }
  }
  const remaining = new Set(next.columns.flat().map((grain) => grain.colour));
  const landed = next.collectors.map((pot) => pot && { ...pot });
  next.collectors = next.collectors.map((pot) => (pot && remaining.has(pot.colour) ? pot : null));
  next.collected += removed;
  next.pots++;
  next.filled += filled;
  next.score += removed * 10 + filled * 250;
  next.complete = next.collected === next.total;
  if (next.complete) next.score += 1000;
  next.lastPot = {
    colour,
    amount: removed,
    capacity,
    full: filled > 0,
    filled,
    filledSlots,
    landed,
  };
  return next;
}

/** Stable zero-based station receiving the next choice; oldest is replaced only
 * when every station is occupied. The UI previews this exact policy. */
export function nextSandSlot(state) {
  const empty = state.collectors.indexOf(null);
  if (empty >= 0) return empty;
  return state.collectors.reduce(
    (oldest, pot, index, pots) => (pot.since < pots[oldest].since ? index : oldest),
    0,
  );
}

/** Public local view exposes exposed pigments and exact conservation counts. */
export function sandView(state) {
  const exposed = Array(16).fill(0),
    remaining = Array(16).fill(0);
  for (const column of state.columns) {
    for (const grain of column) remaining[grain.colour]++;
    if (column.length) exposed[column.at(-1).colour]++;
  }
  return {
    exposed,
    remaining,
    percent: Math.round((state.collected / state.total) * 100),
    complete: state.complete,
  };
}

/** Grains per automatically refilled pot: 64 for standard level-one artwork.
 * Bounds preserve frequent visible rewards without tiny-fixture zero capacity;
 * later levels gradually shorten each pot, without changing removal decisions. */
export function sandPotCapacity(state) {
  return Math.max(24, Math.min(64, Math.ceil(state.total / (88 + (state.level - 1) * 2))));
}

/** Exact immediate cascade sizes for visible decision feedback, in pigment order.
 * Includes existing collectors and the previewed station replacement; future
 * choices, random forecasts and concealed outcomes are never used. */
export function sandHarvest(state) {
  return Array.from(
    { length: 16 },
    (_, colour) => collectSand(state, colour).collected - state.collected,
  );
}
